import React from "react"
import {Trans, useTranslation} from "react-i18next"
import {XMarkIcon} from "@heroicons/react/24/outline"
import {twMerge} from "tailwind-merge"

import {SearchBoxFilter} from "../../../components/filters/SearchBoxFilter.tsx"
import {ConfirmModal} from "../../../components/Modal.tsx"
import {ProfileImage} from "../../../components/ProfileImage.tsx"
import {getCurrentLanguage} from "../../../i18n.ts"
import {getFullName} from "../../../utils"
import {apiDateToJS} from "../../../utils/dateArithmetics.ts"
import {commonTransComponents} from "../../../utils/i18n.tsx"
import {ProspectsContext} from "../shared/context.ts"
import {AdvancedSearchReset} from "./AdvancedSearch/AdvancedSearchReset.tsx"
import {PFLContext} from "./AdvancedSearch/context/context.ts"
import {FilterSidebar} from "./Filters/utilityComponents/FilterSidebar.tsx"

export const ProspectsFromLeadsLayout: React.FC<{
  nextStepButton?: React.ReactNode
  children: React.ReactNode
  hasResults?: boolean
}> = ({nextStepButton, children, hasResults = false}) => {
  const {t} = useTranslation()

  const {onClose, isFiltersOpen, isAdvancedFiltering} = PFLContext.useContext()
  const {salesCycle, assignment, iteration} = ProspectsContext.useContext()
  const [isClosePromptOpen, setIsClosePromptOpen] = React.useState(false)
  const closeConfirmText = t("Prospects_ImportModal_LeadsStep_ClosePrompt_Confirm", {
    defaultValue: "Close and lose results",
  })

  const handleCloseClick = React.useCallback(() => {
    if (hasResults) {
      setIsClosePromptOpen(true)
    } else {
      onClose()
    }
  }, [hasResults, onClose])

  if (assignment == null || iteration == null || salesCycle == null) {
    throw new Error("Error: assignment or iteration not set")
  }

  const assignmentSalesPerson = "sales_person" in assignment ? assignment.sales_person : null

  return (
    <div className={"flex min-h-full grow flex-col gap-10"}>
      <div className={"flex flex-col gap-4"}>
        <div className={"flex items-center justify-between gap-8"}>
          <h1 className={"text-3xl font-semibold text-cr-blue"}>{t("Prospects_ImportModal_LeadsStep_Title")}</h1>
          <div className={"flex items-center gap-4"}>
            <button onClick={handleCloseClick} className={"rounded-full p-2 transition-colors hover:bg-gray-100"}>
              <XMarkIcon className={"size-6 text-gray-600"} />
            </button>
          </div>
        </div>

        <div className={"flex items-center gap-4"}>
          <ProfileImage
            src={assignmentSalesPerson?.profile_picture_thumbnail_url ?? undefined}
            alt={getFullName(assignmentSalesPerson)}
            className={"size-10 shrink-0"}
          />
          <AssignmentBreadcrumbs />
        </div>
      </div>

      <div className={"flex grow flex-col gap-4"}>
        <div className={"flex items-center justify-between gap-8"}>
          <div className={"flex items-center gap-8"}>
            {!isAdvancedFiltering && (
              <SearchBoxFilter borderClassName={twMerge("shrink-0 transition-all", isFiltersOpen ? "w-96" : "w-60")} />
            )}
            {isAdvancedFiltering && <AdvancedSearchReset />}
          </div>

          {nextStepButton}
        </div>

        <div className={"relative flex min-h-96 grow items-stretch gap-4 transition-all"}>
          <div className={"absolute top-0 bottom-0 left-0 shrink-0"}>
            <FilterSidebar />
          </div>
          <div className={twMerge("shrink-0 transition-all", isFiltersOpen ? "w-96" : "w-10")} />
          <div className={twMerge("flex min-w-0 grow flex-col gap-4 transition-all")}>{children}</div>
        </div>
      </div>

      <ConfirmModal
        variant={"warning"}
        isOpen={isClosePromptOpen}
        onClose={() => setIsClosePromptOpen(false)}
        onConfirm={onClose}
        title={t("Prospects_ImportModal_LeadsStep_Advanced_Prompt_Reset_Title")}
        confirmButtonText={closeConfirmText}
      >
        <Trans
          i18nKey={"Prospects_ImportModal_LeadsStep_Advanced_Prompt_Reset_Text"}
          components={commonTransComponents}
        />
      </ConfirmModal>
    </div>
  )
}
export const AssignmentBreadcrumbs: React.FC = () => {
  const {assignment, iteration, salesCycle} = ProspectsContext.useContext()

  if (assignment == null || iteration == null || salesCycle == null) {
    throw new Error("Error: assignment or iteration not set")
  }

  const assignmentSalesPerson = "sales_person" in assignment ? assignment.sales_person : null

  const packageName = "package_name" in assignment ? assignment.package_name : null

  return (
    <span>
      {[
        <span key={"name"} className={"font-bold"}>
          {getFullName(assignmentSalesPerson) || packageName || assignment.prospects_mandatory_count}
        </span>,
        "company" in salesCycle && salesCycle.company?.name,
        salesCycle.name,
        apiDateToJS(iteration.start_date).toLocaleString(getCurrentLanguage(), {
          month: "long",
          year: "numeric",
        }),
      ]
        .filter(Boolean)
        .reduce<React.ReactNode[]>((acc, value) => {
          if (acc.length === 0) {
            return [value]
          }
          return [...acc, " - ", value]
        }, [])
        .map((value, i) => (
          <React.Fragment key={i}>{value}</React.Fragment>
        ))}
    </span>
  )
}
